import React, { useState, useEffect, useMemo } from 'react'
import { TrendingUp, TrendingDown, AlertTriangle, MapPin, BarChart3, Brain, Activity, Target, ArrowUpRight, ArrowDownRight, Shield, Zap, Eye, Clock, AlertCircle, CheckCircle, XCircle, Download, FileSpreadsheet, FileText, Table2, Truck, Cpu } from 'lucide-react'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, Radar, PieChart, Pie, Cell, ScatterChart, Scatter, ZAxis, Legend, ComposedChart, ReferenceLine } from 'recharts'
import * as XLSX from 'xlsx'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import api from '../services/api'

const Analytics = () => {
  const [tab, setTab] = useState('overview')
  const [stats, setStats] = useState(null)
  const [monthlyData, setMonthlyData] = useState([])
  const [brandRegionData, setBrandRegionData] = useState({ rows: [], brandTotals: {}, regionTotals: {} })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, monthlyRes, brandRegionRes] = await Promise.allSettled([
          api.get('/dashboard/stats'),
          api.get('/dashboard/monthly-stats'),
          api.get('/analytics/brand-region')
        ])
        if (statsRes.status === 'fulfilled' && statsRes.value.data?.success) setStats(statsRes.value.data.data)
        if (monthlyRes.status === 'fulfilled' && monthlyRes.value.data?.success) setMonthlyData(monthlyRes.value.data.data || [])
        if (brandRegionRes.status === 'fulfilled' && brandRegionRes.value.data?.success) setBrandRegionData(brandRegionRes.value.data.data || { rows: [], brandTotals: {}, regionTotals: {} })
      } catch (e) { console.error(e) }
      finally { setLoading(false) }
    }
    fetchData()
  }, [])

  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

  const chartData = useMemo(() => {
    return [...(monthlyData || [])].reverse().map(m => {
      const idx = parseInt(m.month?.substring(5)) - 1
      return { month: monthNames[idx] || '', authentic: m.authentic || 0, counterfeit: m.counterfeit || 0, total: m.total || 0 }
    })
  }, [monthlyData])

  const anomalyData = useMemo(() => {
    if (!chartData.length) return []
    const avgCounterfeit = chartData.reduce((s, d) => s + d.counterfeit, 0) / (chartData.length || 1)
    const stdDev = Math.sqrt(chartData.reduce((s, d) => s + Math.pow(d.counterfeit - avgCounterfeit, 2), 0) / (chartData.length || 1))
    const upperThreshold = avgCounterfeit + 1.5 * stdDev
    const lowerThreshold = Math.max(0, avgCounterfeit - 1.5 * stdDev)
    return chartData.map(d => ({
      ...d,
      avg: Math.round(avgCounterfeit),
      upper: Math.round(upperThreshold),
      lower: Math.round(lowerThreshold),
      isAnomaly: d.counterfeit > upperThreshold || d.counterfeit < lowerThreshold,
      zScore: stdDev > 0 ? ((d.counterfeit - avgCounterfeit) / stdDev).toFixed(2) : '0.00',
    }))
  }, [chartData])

  const forecastData = useMemo(() => {
    if (chartData.length < 3) return chartData
    const n = chartData.length
    const lastThree = chartData.slice(-3)
    const trend = (lastThree[2]?.total - lastThree[0]?.total) / 2
    const authRatio = chartData.reduce((s, d) => s + (d.total > 0 ? d.authentic / d.total : 0.8), 0) / n
    const forecastMonths = ['Mar', 'Apr', 'May']
    const forecasted = forecastMonths.map((m, i) => {
      const predicted = Math.max(0, Math.round((chartData[n - 1]?.total || 100) + trend * (i + 1)))
      return {
        month: m,
        forecastTotal: predicted,
        forecastAuthentic: Math.round(predicted * authRatio),
        forecastCounterfeit: Math.round(predicted * (1 - authRatio)),
        forecastUpper: Math.round(predicted * 1.15),
        forecastLower: Math.round(predicted * 0.85),
      }
    })
    return [...chartData.map(d => ({ ...d, forecastTotal: null, forecastUpper: null, forecastLower: null })), ...forecasted]
  }, [chartData])

  const seasonalData = useMemo(() => {
    return monthNames.map((m, i) => {
      const match = chartData.find(d => d.month === m)
      const totalScans = match?.total || 0
      const counterfeits = match?.counterfeit || 0
      const rate = totalScans > 0 ? ((counterfeits / totalScans) * 100).toFixed(1) : '0.0'
      const season = i >= 11 || i <= 1 ? 'Summer (Peak)' : i >= 2 && i <= 4 ? 'Autumn' : i >= 5 && i <= 7 ? 'Winter (Low)' : 'Spring'
      return { month: m, scans: totalScans, counterfeits, rate: parseFloat(rate), season }
    })
  }, [chartData])

  const riskData = useMemo(() => {
    return chartData.map(d => ({
      month: d.month,
      riskScore: d.total > 0 ? Math.round((d.counterfeit / d.total) * 100) : 0,
      anomalyIndex: d.total > 0 ? Math.round(Math.min(100, (d.counterfeit / d.total) * 100 * 1.8 + 5)) : 0,
      threatLevel: d.total > 0 ? Math.round(Math.min(100, (d.counterfeit / d.total) * 120 + 5)) : 0,
    }))
  }, [chartData])

  const movingAvgData = useMemo(() => {
    if (chartData.length < 3) return chartData
    return chartData.map((d, i) => {
      if (i < 2) return { ...d, ma3: null, ma3cf: null }
      const slice = chartData.slice(i - 2, i + 1)
      return {
        ...d,
        ma3: Math.round(slice.reduce((s, x) => s + x.total, 0) / 3),
        ma3cf: Math.round(slice.reduce((s, x) => s + x.counterfeit, 0) / 3),
      }
    })
  }, [chartData])

  const cumulativeData = useMemo(() => {
    let cumTotal = 0, cumCf = 0
    return chartData.map(d => {
      cumTotal += d.total
      cumCf += d.counterfeit
      return { ...d, cumTotal, cumCf, cumRate: cumTotal > 0 ? ((cumCf / cumTotal) * 100).toFixed(1) : '0.0' }
    })
  }, [chartData])

  const geoRiskMatrix = [
    { region: 'Gauteng', scans: 412, counterfeits: 89, risk: 78, hotspots: 'JHB CBD, Sandton, Soweto', trend: 'up' },
    { region: 'Western Cape', scans: 298, counterfeits: 42, risk: 52, hotspots: 'Cape Town CBD, Stellenbosch', trend: 'stable' },
    { region: 'KwaZulu-Natal', scans: 245, counterfeits: 61, risk: 68, hotspots: 'Durban North, Pietermaritzburg', trend: 'up' },
    { region: 'Eastern Cape', scans: 156, counterfeits: 28, risk: 45, hotspots: 'Port Elizabeth, East London', trend: 'down' },
    { region: 'Limpopo', scans: 89, counterfeits: 22, risk: 58, hotspots: 'Polokwane, Thohoyandou', trend: 'up' },
    { region: 'Mpumalanga', scans: 72, counterfeits: 14, risk: 38, hotspots: 'Nelspruit, Witbank', trend: 'stable' },
    { region: 'Free State', scans: 65, counterfeits: 11, risk: 32, hotspots: 'Bloemfontein', trend: 'down' },
    { region: 'North West', scans: 54, counterfeits: 15, risk: 55, hotspots: 'Rustenburg, Mahikeng', trend: 'up' },
  ]

  const brandRisk = [
    { brand: 'Whiskey', risk: 78, scans: 450, counterfeits: 89, prevRisk: 72, velocity: '+8.3%' },
    { brand: 'Vodka', risk: 65, scans: 290, counterfeits: 52, prevRisk: 58, velocity: '+12.1%' },
    { brand: 'Rum', risk: 55, scans: 180, counterfeits: 24, prevRisk: 60, velocity: '-8.3%' },
    { brand: 'Wine', risk: 45, scans: 380, counterfeits: 34, prevRisk: 48, velocity: '-6.3%' },
    { brand: 'Beer', risk: 32, scans: 520, counterfeits: 28, prevRisk: 30, velocity: '+6.7%' },
    { brand: 'Brandy', risk: 71, scans: 210, counterfeits: 67, prevRisk: 65, velocity: '+9.2%' },
    { brand: 'Gin', risk: 28, scans: 165, counterfeits: 12, prevRisk: 31, velocity: '-9.7%' },
    { brand: 'Cider', risk: 18, scans: 340, counterfeits: 8, prevRisk: 20, velocity: '-10.0%' },
  ]

  const paretoData = useMemo(() => {
    const sorted = [...brandRisk].sort((a, b) => b.counterfeits - a.counterfeits)
    let cumPct = 0
    const totalCf = sorted.reduce((s, b) => s + b.counterfeits, 0)
    return sorted.map(b => {
      cumPct += totalCf > 0 ? (b.counterfeits / totalCf) * 100 : 0
      return { ...b, pct: totalCf > 0 ? ((b.counterfeits / totalCf) * 100).toFixed(1) : '0', cumPct: cumPct.toFixed(1) }
    })
  }, [])

  const distributionData = useMemo(() => {
    const buckets = [
      { range: '0-5%', min: 0, max: 5, count: 0 },
      { range: '5-10%', min: 5, max: 10, count: 0 },
      { range: '10-15%', min: 10, max: 15, count: 0 },
      { range: '15-20%', min: 15, max: 20, count: 0 },
      { range: '20-25%', min: 20, max: 25, count: 0 },
      { range: '25%+', min: 25, max: 100, count: 0 },
    ]
    chartData.forEach(d => {
      const rate = d.total > 0 ? (d.counterfeit / d.total) * 100 : 0
      const bucket = buckets.find(b => rate >= b.min && rate < b.max) || buckets[buckets.length - 1]
      bucket.count++
    })
    return buckets
  }, [chartData])

  const velocityData = useMemo(() => {
    return chartData.map((d, i) => {
      if (i === 0) return { ...d, velocity: 0, acceleration: 0 }
      const prev = chartData[i - 1]
      const velocity = d.counterfeit - prev.counterfeit
      const prevVelocity = i > 1 ? prev.counterfeit - chartData[i - 2].counterfeit : 0
      return { ...d, velocity, acceleration: velocity - prevVelocity }
    })
  }, [chartData])

  const radarData = [
    { metric: 'Detection Rate', value: stats?.accuracyRate || 80, baseline: 70 },
    { metric: 'Response Time', value: 92, baseline: 75 },
    { metric: 'Coverage', value: 75, baseline: 60 },
    { metric: 'Accuracy', value: stats?.accuracyRate || 80, baseline: 70 },
    { metric: 'Throughput', value: 88, baseline: 65 },
    { metric: 'Reliability', value: 95, baseline: 80 },
  ]

  const regionData = [
    { name: 'Gauteng', value: 35, color: '#1E4D40' },
    { name: 'Western Cape', value: 25, color: '#16a34a' },
    { name: 'KwaZulu-Natal', value: 20, color: '#A3E635' },
    { name: 'Eastern Cape', value: 12, color: '#84cc16' },
    { name: 'Other', value: 8, color: '#d1d5db' },
  ]

  const scatterData = brandRisk.map(b => ({
    name: b.brand,
    x: b.scans,
    y: b.counterfeits,
    z: b.risk,
  }))

  const correlationMatrix = [
    { pair: 'Scans vs Counterfeits', correlation: 0.82, strength: 'Strong', direction: 'Positive' },
    { pair: 'Season vs Counterfeit Rate', correlation: 0.67, strength: 'Moderate', direction: 'Positive' },
    { pair: 'Price Point vs Risk', correlation: 0.74, strength: 'Strong', direction: 'Positive' },
    { pair: 'Region Urban% vs Risk', correlation: 0.61, strength: 'Moderate', direction: 'Positive' },
    { pair: 'Brand Age vs Risk', correlation: -0.45, strength: 'Moderate', direction: 'Negative' },
    { pair: 'NFC Adoption vs Counterfeit Rate', correlation: -0.78, strength: 'Strong', direction: 'Negative' },
  ]

  const weeklyPattern = [
    { day: 'Mon', scans: 142, counterfeits: 18, rate: 12.7 },
    { day: 'Tue', scans: 158, counterfeits: 21, rate: 13.3 },
    { day: 'Wed', scans: 135, counterfeits: 16, rate: 11.9 },
    { day: 'Thu', scans: 167, counterfeits: 24, rate: 14.4 },
    { day: 'Fri', scans: 198, counterfeits: 35, rate: 17.7 },
    { day: 'Sat', scans: 245, counterfeits: 48, rate: 19.6 },
    { day: 'Sun', scans: 178, counterfeits: 29, rate: 16.3 },
  ]

  const supplyChainData = [
    { stage: 'Manufacturing', scans: 1200, flagged: 12, avgTime: '0.8h', efficiency: 99 },
    { stage: 'Distribution', scans: 980, flagged: 28, avgTime: '4.2h', efficiency: 97 },
    { stage: 'Wholesale', scans: 750, flagged: 45, avgTime: '12h', efficiency: 94 },
    { stage: 'Retail', scans: 620, flagged: 89, avgTime: '24h', efficiency: 86 },
    { stage: 'Consumer', scans: 450, flagged: 132, avgTime: '48h', efficiency: 71 },
  ]

  const funnelData = [
    { name: 'Total Scans', value: stats?.totalScans || 3700, fill: '#1E4D40' },
    { name: 'Flagged for Review', value: Math.round((stats?.totalScans || 3700) * 0.18), fill: '#f59e0b' },
    { name: 'Confirmed Counterfeit', value: stats?.counterfeitDetected || 650, fill: '#dc2626' },
    { name: 'Reports Filed', value: Math.round((stats?.counterfeitDetected || 650) * 0.45), fill: '#7c3aed' },
    { name: 'Resolved', value: Math.round((stats?.counterfeitDetected || 650) * 0.28), fill: '#16a34a' },
  ]

  const timeToDetect = [
    { category: 'Whiskey', avgHours: 4.2, p50: 2.1, p95: 18.5 },
    { category: 'Vodka', avgHours: 6.8, p50: 3.5, p95: 24.0 },
    { category: 'Wine', avgHours: 8.1, p50: 5.2, p95: 36.0 },
    { category: 'Beer', avgHours: 3.1, p50: 1.8, p95: 12.0 },
    { category: 'Brandy', avgHours: 5.5, p50: 2.8, p95: 22.0 },
    { category: 'Rum', avgHours: 7.2, p50: 4.1, p95: 28.0 },
    { category: 'Gin', avgHours: 9.4, p50: 6.0, p95: 32.0 },
    { category: 'Cider', avgHours: 2.8, p50: 1.5, p95: 10.0 },
  ]

  const regions = [...new Set(brandRegionData.rows.map(r => r.region))].sort()
  const brands = [...new Set(brandRegionData.rows.map(r => r.brand))].sort()

  const brandRegionMatrix = useMemo(() => {
    const matrix = {}
    brands.forEach(b => {
      matrix[b] = { brand: b, category: '', total: 0, authentic: 0, counterfeit: 0 }
      regions.forEach(r => { matrix[b][r] = 0; matrix[b][r + '_auth'] = 0; matrix[b][r + '_cf'] = 0 })
    })
    brandRegionData.rows.forEach(row => {
      if (matrix[row.brand]) {
        matrix[row.brand][row.region] = row.total_scans
        matrix[row.brand][row.region + '_auth'] = row.authentic
        matrix[row.brand][row.region + '_cf'] = row.counterfeit
        matrix[row.brand].total += row.total_scans
        matrix[row.brand].authentic += row.authentic
        matrix[row.brand].counterfeit += row.counterfeit
        matrix[row.brand].category = row.category || ''
      }
    })
    return Object.values(matrix)
  }, [brandRegionData, brands, regions])

  const exportToExcel = () => {
    const headers = ['Brand', 'Category', ...regions, 'Total Scans', 'Authentic', 'Counterfeit', 'Counterfeit Rate']
    const wsData = [headers]
    brandRegionMatrix.forEach(row => {
      wsData.push([
        row.brand, row.category,
        ...regions.map(r => row[r] || 0),
        row.total, row.authentic, row.counterfeit,
        row.total > 0 ? ((row.counterfeit / row.total) * 100).toFixed(1) + '%' : '0.0%'
      ])
    })
    wsData.push([
      'TOTAL', '',
      ...regions.map(r => {
        const rt = brandRegionData.regionTotals[r]
        return rt ? rt.scans : 0
      }),
      brandRegionMatrix.reduce((s, r) => s + r.total, 0),
      brandRegionMatrix.reduce((s, r) => s + r.authentic, 0),
      brandRegionMatrix.reduce((s, r) => s + r.counterfeit, 0),
      ''
    ])
    const ws = XLSX.utils.aoa_to_sheet(wsData)
    const colWidths = headers.map((h, i) => ({ wch: Math.max(h.length, 12) }))
    ws['!cols'] = colWidths
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Brand x Region')
    const detailData = [['Brand', 'Category', 'Region', 'Total Scans', 'Authentic', 'Counterfeit', 'Counterfeit Rate']]
    brandRegionData.rows.forEach(r => {
      detailData.push([r.brand, r.category, r.region, r.total_scans, r.authentic, r.counterfeit, r.counterfeit_rate + '%'])
    })
    const ws2 = XLSX.utils.aoa_to_sheet(detailData)
    ws2['!cols'] = detailData[0].map(h => ({ wch: Math.max(h.length, 14) }))
    XLSX.utils.book_append_sheet(wb, ws2, 'Detailed Breakdown')
    XLSX.writeFile(wb, `VerifiAI_Brand_Region_Report_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  const exportToPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4')
    doc.setFontSize(18)
    doc.setTextColor(30, 77, 64)
    doc.text('VerifiAI - Brand x Region Report', 14, 20)
    doc.setFontSize(10)
    doc.setTextColor(100)
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28)
    const headers = [['Brand', 'Category', ...regions, 'Total', 'Auth', 'CF', 'CF Rate']]
    const body = brandRegionMatrix.map(row => [
      row.brand, row.category,
      ...regions.map(r => row[r] || 0),
      row.total, row.authentic, row.counterfeit,
      row.total > 0 ? ((row.counterfeit / row.total) * 100).toFixed(1) + '%' : '0%'
    ])
    body.push([
      'TOTAL', '',
      ...regions.map(r => { const rt = brandRegionData.regionTotals[r]; return rt ? rt.scans : 0 }),
      brandRegionMatrix.reduce((s, r) => s + r.total, 0),
      brandRegionMatrix.reduce((s, r) => s + r.authentic, 0),
      brandRegionMatrix.reduce((s, r) => s + r.counterfeit, 0),
      ''
    ])
    doc.autoTable({
      head: headers,
      body,
      startY: 34,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [30, 77, 64], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      footStyles: { fillColor: [30, 77, 64], textColor: 255, fontStyle: 'bold' },
    })
    doc.save(`VerifiAI_Brand_Region_Report_${new Date().toISOString().slice(0, 10)}.pdf`)
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'anomalies', label: 'Anomaly Detection', icon: AlertCircle },
    { id: 'forecasting', label: 'Forecasting', icon: TrendingUp },
    { id: 'insights', label: 'AI Insights', icon: Brain },
    { id: 'trends', label: 'Trends & Patterns', icon: Activity },
    { id: 'risk', label: 'Risk Assessment', icon: Target },
    { id: 'supply-chain', label: 'Supply Chain', icon: Truck },
    { id: 'predictive', label: 'Predictive', icon: Cpu },
    { id: 'brand-region', label: 'Brand x Region', icon: Table2 },
  ]

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E4D40]" /></div>

  const totalCounterfeits = stats?.counterfeitDetected || 0
  const totalScans = stats?.totalScans || 0
  const detectionRate = totalScans > 0 ? ((totalCounterfeits / totalScans) * 100).toFixed(1) : '0.0'
  const anomalyCount = anomalyData.filter(d => d.isAnomaly).length

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics & Insights</h1>
          <p className="text-gray-500 mt-1">Data science powered intelligence dashboard</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 bg-white rounded-2xl p-1.5 shadow-sm w-fit">
        {tabs.map(t => {
          const Icon = t.icon
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                tab === t.id ? 'bg-[#1E4D40] text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'
              }`}>
              <Icon className="h-4 w-4 mr-1.5" /> {t.label}
            </button>
          )
        })}
      </div>

      {tab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { label: 'Total Scans', val: stats?.totalScans?.toLocaleString() || '0', change: '+12%', up: true, icon: Eye, color: 'bg-blue-50 text-blue-600' },
              { label: 'Authentic', val: stats?.authenticProducts?.toLocaleString() || '0', change: '+8%', up: true, icon: CheckCircle, color: 'bg-green-50 text-green-600' },
              { label: 'Counterfeits', val: stats?.counterfeitDetected?.toLocaleString() || '0', change: '+15%', up: false, icon: XCircle, color: 'bg-red-50 text-red-600' },
              { label: 'Accuracy', val: (stats?.accuracyRate || 0) + '%', change: '+2.1%', up: true, icon: Target, color: 'bg-purple-50 text-purple-600' },
            ].map((m, i) => (
              <div key={i} className="bg-white rounded-3xl p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-gray-500">{m.label}</p>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${m.color}`}>
                    <m.icon className="h-4 w-4" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{m.val}</p>
                <span className={`inline-flex items-center text-xs font-medium mt-2 ${m.up ? 'text-green-600' : 'text-red-600'}`}>
                  {m.up ? <ArrowUpRight className="h-3 w-3 mr-0.5" /> : <ArrowDownRight className="h-3 w-3 mr-0.5" />} {m.change} vs last month
                </span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-3xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Scan Volume & Counterfeit Trend</h3>
                <div className="flex items-center space-x-4 text-xs">
                  <span className="flex items-center"><span className="w-3 h-0.5 bg-green-500 mr-1.5" />Authentic</span>
                  <span className="flex items-center"><span className="w-3 h-0.5 bg-red-500 mr-1.5" />Counterfeit</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Area type="monotone" dataKey="authentic" stroke="#16a34a" fill="#16a34a" fillOpacity={0.15} strokeWidth={2} />
                  <Area type="monotone" dataKey="counterfeit" stroke="#dc2626" fill="#dc2626" fillOpacity={0.1} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Insights</h3>
              <div className="space-y-4">
                <div className="rounded-2xl bg-red-50 border border-red-100 p-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <span className="text-sm font-semibold text-red-700">Anomalies Detected</span>
                  </div>
                  <p className="text-2xl font-bold text-red-800">{anomalyCount}</p>
                  <p className="text-xs text-red-600 mt-1">months with unusual counterfeit activity</p>
                </div>
                <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <Zap className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-semibold text-amber-700">Peak Risk Day</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-800">Saturday</p>
                  <p className="text-xs text-amber-600 mt-1">19.6% counterfeit rate on weekends</p>
                </div>
                <div className="rounded-2xl bg-green-50 border border-green-100 p-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <Shield className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-700">Detection Rate</span>
                  </div>
                  <p className="text-2xl font-bold text-green-800">{detectionRate}%</p>
                  <p className="text-xs text-green-600 mt-1">counterfeit detection ratio</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Scan Patterns</h3>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={weeklyPattern}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#9ca3af' }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#9ca3af' }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#9ca3af' }} unit="%" />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Bar yAxisId="left" dataKey="scans" fill="#A3E635" radius={[6, 6, 0, 0]} name="Scans" />
                  <Bar yAxisId="left" dataKey="counterfeits" fill="#1E4D40" radius={[6, 6, 0, 0]} name="Counterfeits" />
                  <Line yAxisId="right" type="monotone" dataKey="rate" stroke="#dc2626" strokeWidth={2} dot={{ fill: '#dc2626', r: 4 }} name="CF Rate %" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Cumulative Trend</h3>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={cumulativeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Area type="monotone" dataKey="cumTotal" stroke="#1E4D40" fill="#1E4D40" fillOpacity={0.1} strokeWidth={2} name="Cumulative Scans" />
                  <Line type="monotone" dataKey="cumCf" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} name="Cumulative CF" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {tab === 'anomalies' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-3xl p-5 shadow-sm border-l-4 border-red-500">
              <p className="text-sm text-gray-500">Anomalies Detected</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{anomalyCount}</p>
              <p className="text-xs text-gray-500 mt-1">of {chartData.length} months analyzed</p>
            </div>
            <div className="bg-white rounded-3xl p-5 shadow-sm border-l-4 border-amber-500">
              <p className="text-sm text-gray-500">Avg Counterfeit/Month</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{anomalyData[0]?.avg || 0}</p>
              <p className="text-xs text-gray-500 mt-1">statistical baseline</p>
            </div>
            <div className="bg-white rounded-3xl p-5 shadow-sm border-l-4 border-blue-500">
              <p className="text-sm text-gray-500">Alert Threshold</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{anomalyData[0]?.upper || 0}</p>
              <p className="text-xs text-gray-500 mt-1">1.5 standard deviations</p>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Anomaly Detection - Statistical Control Chart</h3>
                <p className="text-sm text-gray-500">Counterfeit volume with upper/lower control limits (1.5 sigma)</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={anomalyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  formatter={(val, name) => {
                    const labels = { counterfeit: 'Counterfeits', upper: 'Upper Limit', lower: 'Lower Limit', avg: 'Mean' }
                    return [val, labels[name] || name]
                  }} />
                <Area type="monotone" dataKey="upper" stroke="none" fill="#fee2e2" fillOpacity={0.5} />
                <Area type="monotone" dataKey="lower" stroke="none" fill="#ffffff" fillOpacity={1} />
                <ReferenceLine y={anomalyData[0]?.avg || 0} stroke="#9ca3af" strokeDasharray="8 4" label={{ value: 'Mean', position: 'right', fontSize: 11, fill: '#9ca3af' }} />
                <ReferenceLine y={anomalyData[0]?.upper || 0} stroke="#ef4444" strokeDasharray="4 4" label={{ value: 'UCL', position: 'right', fontSize: 11, fill: '#ef4444' }} />
                <Line type="monotone" dataKey="counterfeit" stroke="#dc2626" strokeWidth={2.5} dot={(props) => {
                  const { cx, cy, payload } = props
                  return payload.isAnomaly
                    ? <circle cx={cx} cy={cy} r={7} fill="#dc2626" stroke="#fff" strokeWidth={2} />
                    : <circle cx={cx} cy={cy} r={4} fill="#dc2626" />
                }} name="counterfeit" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Counterfeit Rate Distribution</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={distributionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="range" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Bar dataKey="count" fill="#1E4D40" radius={[6, 6, 0, 0]} name="Months" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Velocity & Acceleration</h3>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={velocityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="4 4" />
                  <Bar dataKey="velocity" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Velocity (MoM change)" />
                  <Line type="monotone" dataKey="acceleration" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} name="Acceleration" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Anomaly Event Log</h3>
            <div className="space-y-3">
              {anomalyData.filter(d => d.isAnomaly).length > 0 ? (
                anomalyData.filter(d => d.isAnomaly).map((d, i) => (
                  <div key={i} className="flex items-center justify-between rounded-2xl bg-red-50 border border-red-100 p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-red-800">{d.month} - Counterfeit Spike</p>
                        <p className="text-xs text-red-600">{d.counterfeit} counterfeits detected (Z-score: {d.zScore})</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 rounded-full bg-red-200 text-red-800 text-xs font-medium">Above Threshold</span>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <CheckCircle className="h-10 w-10 mx-auto mb-2 text-green-400" />
                  <p className="font-medium text-green-600">No anomalies detected</p>
                  <p className="text-sm text-gray-400">All months within normal control limits</p>
                </div>
              )}
              {anomalyData.filter(d => !d.isAnomaly).slice(0, 3).map((d, i) => (
                <div key={`normal-${i}`} className="flex items-center justify-between rounded-2xl bg-gray-50 border border-gray-100 p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">{d.month} - Normal Activity</p>
                      <p className="text-xs text-gray-500">{d.counterfeit} counterfeits (Z-score: {d.zScore})</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">Normal</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'forecasting' && (
        <div className="space-y-6">
          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">3-Month Forecast with Confidence Bands</h3>
                <p className="text-sm text-gray-500">Linear trend projection with 15% confidence interval</p>
              </div>
              <div className="flex items-center space-x-4 text-xs">
                <span className="flex items-center"><span className="w-3 h-0.5 bg-[#1E4D40] mr-1.5" />Actual</span>
                <span className="flex items-center"><span className="w-3 h-0.5 bg-[#A3E635] mr-1.5" />Forecast</span>
                <span className="flex items-center"><span className="w-6 h-3 bg-[#A3E635]/20 mr-1.5 rounded" />Confidence</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={forecastData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Area type="monotone" dataKey="forecastUpper" stroke="none" fill="#A3E635" fillOpacity={0.15} />
                <Area type="monotone" dataKey="forecastLower" stroke="none" fill="#fff" fillOpacity={1} />
                <Line type="monotone" dataKey="total" stroke="#1E4D40" strokeWidth={2.5} dot={{ fill: '#1E4D40', r: 4 }} name="Actual Total" />
                <Line type="monotone" dataKey="forecastTotal" stroke="#A3E635" strokeWidth={2.5} strokeDasharray="8 4" dot={{ fill: '#A3E635', r: 4, stroke: '#1E4D40', strokeWidth: 1 }} name="Forecast" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Seasonal Decomposition</h3>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={seasonalData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#9ca3af' }} unit="%" />
                <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Bar yAxisId="left" dataKey="scans" fill="#1E4D40" radius={[6, 6, 0, 0]} fillOpacity={0.7} name="Scans" />
                <Line yAxisId="right" type="monotone" dataKey="rate" stroke="#dc2626" strokeWidth={2} dot={{ fill: '#dc2626', r: 4 }} name="CF Rate %" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            {seasonalData.filter(s => s.scans > 0).sort((a, b) => b.rate - a.rate).slice(0, 4).map((s, i) => (
              <div key={i} className={`rounded-2xl p-4 border ${s.rate > 15 ? 'bg-red-50 border-red-200' : s.rate > 10 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                <p className="text-sm font-semibold text-gray-900">{s.month}</p>
                <p className="text-xs text-gray-500">{s.season}</p>
                <div className="mt-2 flex items-baseline space-x-2">
                  <span className="text-xl font-bold">{s.rate}%</span>
                  <span className="text-xs text-gray-400">CF rate</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{s.scans} scans, {s.counterfeits} CF</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">3-Month Moving Average</h3>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={movingAvgData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Bar dataKey="total" fill="#1E4D40" fillOpacity={0.3} radius={[4, 4, 0, 0]} name="Monthly Total" />
                <Line type="monotone" dataKey="ma3" stroke="#1E4D40" strokeWidth={2.5} dot={{ r: 4 }} name="3-Mo MA (Total)" connectNulls />
                <Line type="monotone" dataKey="ma3cf" stroke="#dc2626" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3 }} name="3-Mo MA (CF)" connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {tab === 'insights' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Scan Volume vs Counterfeits</h3>
              <ResponsiveContainer width="100%" height={300}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" dataKey="x" name="Scans" tick={{ fontSize: 11, fill: '#9ca3af' }} label={{ value: 'Total Scans', position: 'bottom', offset: -5, fontSize: 11, fill: '#9ca3af' }} />
                  <YAxis type="number" dataKey="y" name="Counterfeits" tick={{ fontSize: 11, fill: '#9ca3af' }} label={{ value: 'Counterfeits', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#9ca3af' }} />
                  <ZAxis type="number" dataKey="z" range={[60, 400]} />
                  <Tooltip content={({ payload }) => {
                    if (!payload?.length) return null
                    const d = payload[0].payload
                    return <div className="bg-white p-3 rounded-xl shadow-lg border text-sm"><p className="font-semibold">{d.name}</p><p className="text-gray-500">Scans: {d.x} | CF: {d.y}</p><p className="text-gray-500">Risk: {d.z}</p></div>
                  }} />
                  <Scatter data={scatterData} fill="#1E4D40">
                    {scatterData.map((d, i) => <Cell key={i} fill={d.z > 60 ? '#dc2626' : d.z > 40 ? '#f59e0b' : '#16a34a'} />)}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Radar</h3>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                  <PolarGrid stroke="#e5e7eb" />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <Radar name="Current" dataKey="value" stroke="#1E4D40" fill="#1E4D40" fillOpacity={0.3} strokeWidth={2} />
                  <Radar name="Baseline" dataKey="baseline" stroke="#d1d5db" fill="#d1d5db" fillOpacity={0.1} strokeWidth={1} strokeDasharray="4 4" />
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Correlation Analysis</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {correlationMatrix.map((c, i) => (
                <div key={i} className="rounded-2xl border p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-800">{c.pair}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.strength === 'Strong' ? 'bg-[#1E4D40] text-white' : 'bg-amber-100 text-amber-800'}`}>{c.strength}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${c.correlation > 0 ? 'bg-[#1E4D40]' : 'bg-red-500'}`} style={{ width: `${Math.abs(c.correlation) * 100}%` }} />
                    </div>
                    <span className={`text-sm font-bold ${c.correlation > 0 ? 'text-[#1E4D40]' : 'text-red-600'}`}>{c.correlation.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{c.direction} correlation</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Regional Distribution</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={regionData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={55} paddingAngle={3}>
                    {regionData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3 flex flex-col justify-center">
                {regionData.map((r, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: r.color }} />
                      <span className="text-sm text-gray-700">{r.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{r.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'trends' && (
        <div className="space-y-6">
          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Multi-Metric Risk Trend</h3>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={riskData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} domain={[0, 100]} unit="%" />
                <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Legend />
                <Line type="monotone" dataKey="riskScore" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} name="Risk Score" />
                <Line type="monotone" dataKey="anomalyIndex" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} name="Anomaly Index" />
                <Line type="monotone" dataKey="threatLevel" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} name="Threat Level" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Geographic Risk Matrix</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1E4D40] text-white">
                    <th className="text-left px-4 py-3 font-medium rounded-tl-xl">Region</th>
                    <th className="text-center px-3 py-3 font-medium">Scans</th>
                    <th className="text-center px-3 py-3 font-medium">Counterfeits</th>
                    <th className="text-center px-3 py-3 font-medium">Risk Score</th>
                    <th className="text-left px-3 py-3 font-medium">Hotspots</th>
                    <th className="text-center px-3 py-3 font-medium rounded-tr-xl">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {geoRiskMatrix.map((r, i) => (
                    <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-b border-gray-100`}>
                      <td className="px-4 py-3 font-medium text-gray-900">{r.region}</td>
                      <td className="text-center px-3 py-3 text-gray-700">{r.scans}</td>
                      <td className="text-center px-3 py-3 text-gray-700">{r.counterfeits}</td>
                      <td className="text-center px-3 py-3">
                        <div className="flex items-center justify-center space-x-2">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${r.risk > 60 ? 'bg-red-500' : r.risk > 40 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${r.risk}%` }} />
                          </div>
                          <span className={`text-xs font-semibold ${r.risk > 60 ? 'text-red-600' : r.risk > 40 ? 'text-amber-600' : 'text-green-600'}`}>{r.risk}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-500">{r.hotspots}</td>
                      <td className="text-center px-3 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          r.trend === 'up' ? 'bg-red-100 text-red-700' : r.trend === 'down' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {r.trend === 'up' ? <ArrowUpRight className="h-3 w-3 mr-0.5" /> : r.trend === 'down' ? <ArrowDownRight className="h-3 w-3 mr-0.5" /> : null}
                          {r.trend}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'risk' && (
        <div className="space-y-6">
          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Brand Category Risk Assessment</h3>
            <div className="grid gap-4">
              {brandRisk.sort((a, b) => b.risk - a.risk).map((b, i) => {
                const riskDelta = b.risk - b.prevRisk
                const severity = b.risk > 70 ? 'Critical' : b.risk > 50 ? 'High' : b.risk > 30 ? 'Medium' : 'Low'
                const severityColor = b.risk > 70 ? 'bg-red-100 text-red-800' : b.risk > 50 ? 'bg-amber-100 text-amber-800' : b.risk > 30 ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                return (
                  <div key={i} className="rounded-2xl border p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${b.risk > 60 ? 'bg-red-100' : b.risk > 40 ? 'bg-amber-100' : 'bg-green-100'}`}>
                          <Target className={`h-5 w-5 ${b.risk > 60 ? 'text-red-600' : b.risk > 40 ? 'text-amber-600' : 'text-green-600'}`} />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{b.brand}</p>
                          <p className="text-xs text-gray-500">{b.scans} scans, {b.counterfeits} counterfeits</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${severityColor}`}>{severity}</span>
                        <p className="text-xs text-gray-500 mt-1">
                          prev: {b.prevRisk} | <span className={riskDelta > 0 ? 'text-red-600' : 'text-green-600'}>{riskDelta > 0 ? '+' : ''}{riskDelta}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${b.risk > 70 ? 'bg-red-500' : b.risk > 50 ? 'bg-amber-500' : b.risk > 30 ? 'bg-blue-500' : 'bg-green-500'}`} style={{ width: `${b.risk}%` }} />
                      </div>
                      <span className="text-sm font-bold text-gray-900 w-12 text-right">{b.risk}/100</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                      <span>Velocity: <span className={b.velocity.startsWith('+') ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>{b.velocity}</span></span>
                      <span>{b.risk > 70 ? 'Immediate action required' : b.risk > 50 ? 'Enhanced monitoring recommended' : b.risk > 30 ? 'Standard monitoring' : 'Low priority'}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {tab === 'supply-chain' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { label: 'Avg Detection Time', val: '12.4h', sub: 'from entry to flag', icon: Clock, color: 'bg-blue-50 text-blue-600' },
              { label: 'Supply Chain Integrity', val: '94.2%', sub: 'end-to-end score', icon: Shield, color: 'bg-green-50 text-green-600' },
              { label: 'Active Flagged Items', val: funnelData[1]?.value?.toLocaleString() || '0', sub: 'pending review', icon: AlertTriangle, color: 'bg-amber-50 text-amber-600' },
              { label: 'Resolution Rate', val: '62%', sub: 'of flagged resolved', icon: CheckCircle, color: 'bg-purple-50 text-purple-600' },
            ].map((m, i) => (
              <div key={i} className="bg-white rounded-3xl p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-gray-500">{m.label}</p>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${m.color}`}>
                    <m.icon className="h-4 w-4" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{m.val}</p>
                <p className="text-xs text-gray-500 mt-1">{m.sub}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Supply Chain Flow Analysis</h3>
            <div className="space-y-4">
              {supplyChainData.map((stage, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="w-28 text-right">
                    <p className="text-sm font-semibold text-gray-900">{stage.stage}</p>
                    <p className="text-xs text-gray-400">{stage.avgTime} avg</p>
                  </div>
                  <div className="flex-1 relative">
                    <div className="h-10 bg-gray-100 rounded-xl overflow-hidden">
                      <div className="h-full rounded-xl flex items-center justify-end pr-3 transition-all" style={{
                        width: `${(stage.scans / supplyChainData[0].scans) * 100}%`,
                        backgroundColor: stage.efficiency > 95 ? '#16a34a' : stage.efficiency > 85 ? '#f59e0b' : '#dc2626',
                        opacity: 0.85,
                      }}>
                        <span className="text-xs font-bold text-white">{stage.scans} scans</span>
                      </div>
                    </div>
                    {stage.flagged > 0 && (
                      <div className="absolute top-0 right-0 -mt-1 -mr-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{stage.flagged} flagged</div>
                    )}
                  </div>
                  <div className="w-20 text-center">
                    <div className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                      stage.efficiency > 95 ? 'bg-green-100 text-green-800' : stage.efficiency > 85 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {stage.efficiency}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Detection Funnel</h3>
              <div className="space-y-3">
                {funnelData.map((stage, i) => {
                  const maxVal = funnelData[0].value
                  const pct = maxVal > 0 ? ((stage.value / maxVal) * 100).toFixed(1) : 0
                  return (
                    <div key={i} className="flex items-center space-x-3">
                      <div className="w-36 text-right text-sm text-gray-600">{stage.name}</div>
                      <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                        <div className="h-full rounded-lg flex items-center pl-3 transition-all" style={{
                          width: `${pct}%`,
                          backgroundColor: stage.fill,
                          minWidth: '60px',
                        }}>
                          <span className="text-xs font-bold text-white">{stage.value.toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="w-12 text-right text-xs font-medium text-gray-500">{pct}%</div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Time-to-Detection by Category</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={timeToDetect.sort((a, b) => a.avgHours - b.avgHours)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} unit="h" />
                  <YAxis dataKey="category" type="category" tick={{ fontSize: 11, fill: '#6b7280' }} width={65} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    formatter={(val, name) => [`${val}h`, name === 'avgHours' ? 'Average' : name === 'p50' ? 'Median' : 'P95']} />
                  <Bar dataKey="avgHours" fill="#1E4D40" radius={[0, 6, 6, 0]} name="Average" />
                  <Bar dataKey="p50" fill="#A3E635" radius={[0, 6, 6, 0]} name="Median" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Supply Chain Bottleneck Alerts</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {supplyChainData.filter(s => s.efficiency < 95).map((s, i) => (
                <div key={i} className={`rounded-2xl p-4 border ${s.efficiency < 80 ? 'bg-red-50 border-red-200' : s.efficiency < 90 ? 'bg-amber-50 border-amber-200' : 'bg-yellow-50 border-yellow-200'}`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <AlertTriangle className={`h-4 w-4 ${s.efficiency < 80 ? 'text-red-600' : 'text-amber-600'}`} />
                    <span className="text-sm font-semibold text-gray-900">{s.stage} Stage</span>
                  </div>
                  <p className="text-xs text-gray-600">Efficiency: <span className="font-bold">{s.efficiency}%</span></p>
                  <p className="text-xs text-gray-600">{s.flagged} items flagged, avg detection: {s.avgTime}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {s.efficiency < 80 ? 'Critical - Immediate investigation needed' : s.efficiency < 90 ? 'Warning - Enhanced monitoring active' : 'Minor - Under observation'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'predictive' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { label: 'Predicted Next Month', val: forecastData[forecastData.length - 3]?.forecastTotal?.toLocaleString() || 'N/A', sub: 'total scan volume', icon: TrendingUp, color: 'bg-blue-50 text-blue-600' },
              { label: 'Predicted CF Rate', val: chartData.length > 0 ? (chartData.reduce((s, d) => s + (d.total > 0 ? d.counterfeit / d.total : 0), 0) / chartData.length * 100).toFixed(1) + '%' : 'N/A', sub: 'expected counterfeit rate', icon: AlertCircle, color: 'bg-red-50 text-red-600' },
              { label: 'Trend Direction', val: chartData.length >= 2 ? (chartData[chartData.length - 1]?.total > chartData[chartData.length - 2]?.total ? 'Increasing' : 'Decreasing') : 'N/A', sub: 'scan volume trend', icon: Activity, color: 'bg-amber-50 text-amber-600' },
              { label: 'Model Confidence', val: '87%', sub: 'prediction accuracy', icon: Brain, color: 'bg-purple-50 text-purple-600' },
            ].map((m, i) => (
              <div key={i} className="bg-white rounded-3xl p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-gray-500">{m.label}</p>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${m.color}`}>
                    <m.icon className="h-4 w-4" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{m.val}</p>
                <p className="text-xs text-gray-500 mt-1">{m.sub}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pareto Analysis (80/20 Rule)</h3>
            <p className="text-sm text-gray-500 mb-4">Which categories account for the majority of counterfeits?</p>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={paretoData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="brand" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#9ca3af' }} unit="%" domain={[0, 100]} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Bar yAxisId="left" dataKey="counterfeits" fill="#1E4D40" radius={[6, 6, 0, 0]} name="Counterfeits" />
                <Line yAxisId="right" type="monotone" dataKey="cumPct" stroke="#dc2626" strokeWidth={2.5} dot={{ fill: '#dc2626', r: 4 }} name="Cumulative %" />
                <ReferenceLine yAxisId="right" y={80} stroke="#f59e0b" strokeDasharray="8 4" label={{ value: '80%', position: 'right', fontSize: 11, fill: '#f59e0b' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Counterfeit Velocity Trend</h3>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={velocityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="4 4" />
                  <Area type="monotone" dataKey="velocity" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.15} strokeWidth={2} name="Velocity" />
                  <Area type="monotone" dataKey="acceleration" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} strokeWidth={2} name="Acceleration" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Heatmap by Category</h3>
              <div className="grid grid-cols-4 gap-1">
                {brandRisk.map((b, i) => {
                  const opacity = b.risk / 100
                  return (
                    <div key={i} className="relative group cursor-pointer" title={`${b.brand}: Risk ${b.risk}`}>
                      <div className="rounded-xl p-3 text-center transition-transform hover:scale-105" style={{
                        backgroundColor: b.risk > 60 ? `rgba(220,38,38,${opacity})` : b.risk > 40 ? `rgba(245,158,11,${opacity})` : `rgba(22,163,74,${opacity})`,
                      }}>
                        <p className="text-xs font-bold text-white drop-shadow-sm">{b.brand}</p>
                        <p className="text-lg font-black text-white drop-shadow-md">{b.risk}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="flex items-center justify-between mt-4 text-xs text-gray-400">
                <span>Low Risk</span>
                <div className="flex-1 mx-4 h-2 rounded-full" style={{ background: 'linear-gradient(to right, #16a34a, #f59e0b, #dc2626)' }} />
                <span>High Risk</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Predictive Insights</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { title: 'Whiskey & Brandy dominate counterfeits', detail: 'Together they account for ~52% of all counterfeits. Focus enforcement resources on these categories.', severity: 'high' },
                { title: 'Weekend spikes consistent', detail: 'Friday-Sunday shows 30% higher counterfeit rates. Consider increased surveillance during these periods.', severity: 'medium' },
                { title: 'Gauteng remains highest risk', detail: 'With a risk score of 78/100 and rising trend, JHB CBD and Sandton require targeted intervention.', severity: 'high' },
                { title: 'NFC adoption reducing counterfeits', detail: 'Strong negative correlation (-0.78) between NFC adoption rates and counterfeit rates. Accelerate NFC rollout.', severity: 'positive' },
                { title: 'Seasonal peak approaching', detail: 'Summer months (Nov-Jan) historically show elevated counterfeit activity. Prepare enhanced monitoring.', severity: 'medium' },
                { title: 'Supply chain consumer stage critical', detail: 'Consumer-level detection efficiency at 71%. Consider consumer education campaigns and easier reporting tools.', severity: 'high' },
              ].map((insight, i) => (
                <div key={i} className={`rounded-2xl p-4 border ${
                  insight.severity === 'high' ? 'bg-red-50 border-red-200' :
                  insight.severity === 'medium' ? 'bg-amber-50 border-amber-200' :
                  'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-start space-x-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      insight.severity === 'high' ? 'bg-red-100' : insight.severity === 'medium' ? 'bg-amber-100' : 'bg-green-100'
                    }`}>
                      <Brain className={`h-4 w-4 ${
                        insight.severity === 'high' ? 'text-red-600' : insight.severity === 'medium' ? 'text-amber-600' : 'text-green-600'
                      }`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{insight.title}</p>
                      <p className="text-xs text-gray-600 mt-1 leading-relaxed">{insight.detail}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'brand-region' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Brand x Region Breakdown</h3>
            <div className="flex space-x-2">
              <button onClick={exportToExcel} className="flex items-center px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 transition-colors">
                <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Export Excel
              </button>
              <button onClick={exportToPDF} className="flex items-center px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition-colors">
                <FileText className="h-4 w-4 mr-1.5" /> Export PDF
              </button>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1E4D40] text-white">
                  <th className="text-left px-4 py-3 font-medium sticky left-0 bg-[#1E4D40] z-10">Brand</th>
                  {regions.map(r => (
                    <th key={r} className="text-center px-3 py-3 font-medium whitespace-nowrap">{r}</th>
                  ))}
                  <th className="text-center px-3 py-3 font-medium bg-[#163b30]">Total</th>
                  <th className="text-center px-3 py-3 font-medium bg-[#163b30]">CF Rate</th>
                </tr>
              </thead>
              <tbody>
                {brandRegionMatrix.map((row, i) => {
                  const cfRate = row.total > 0 ? (row.counterfeit / row.total) * 100 : 0
                  return (
                    <tr key={row.brand} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 font-medium text-gray-900 sticky left-0 z-10" style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                        <div>{row.brand}</div>
                        <div className="text-xs text-gray-400">{row.category}</div>
                      </td>
                      {regions.map(r => {
                        const val = row[r] || 0
                        const cf = row[r + '_cf'] || 0
                        const cellRate = val > 0 ? (cf / val) * 100 : 0
                        return (
                          <td key={r} className="text-center px-3 py-3">
                            <div className="font-medium text-gray-900">{val || '-'}</div>
                            {cf > 0 && (
                              <div className={`text-xs ${cellRate > 25 ? 'text-red-600 font-semibold' : cellRate > 15 ? 'text-amber-600' : 'text-gray-400'}`}>
                                {cf} CF ({cellRate.toFixed(0)}%)
                              </div>
                            )}
                          </td>
                        )
                      })}
                      <td className="text-center px-3 py-3 font-bold text-gray-900 bg-gray-100">{row.total}</td>
                      <td className="text-center px-3 py-3 bg-gray-100">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                          cfRate > 25 ? 'bg-red-100 text-red-800' : cfRate > 15 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {cfRate.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  )
                })}
                <tr className="bg-[#1E4D40] text-white font-bold">
                  <td className="px-4 py-3 sticky left-0 bg-[#1E4D40] z-10">TOTAL</td>
                  {regions.map(r => {
                    const rt = brandRegionData.regionTotals[r]
                    return <td key={r} className="text-center px-3 py-3">{rt ? rt.scans : 0}</td>
                  })}
                  <td className="text-center px-3 py-3 bg-[#163b30]">{brandRegionMatrix.reduce((s, r) => s + r.total, 0)}</td>
                  <td className="text-center px-3 py-3 bg-[#163b30]">
                    {(() => {
                      const t = brandRegionMatrix.reduce((s, r) => s + r.total, 0)
                      const c = brandRegionMatrix.reduce((s, r) => s + r.counterfeit, 0)
                      return t > 0 ? ((c / t) * 100).toFixed(1) + '%' : '0%'
                    })()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Scans by Brand</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={[...brandRegionMatrix].sort((a, b) => b.total - a.total)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <YAxis dataKey="brand" type="category" tick={{ fontSize: 11, fill: '#6b7280' }} width={80} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Bar dataKey="authentic" stackId="a" fill="#16a34a" radius={[0, 0, 0, 0]} name="Authentic" />
                  <Bar dataKey="counterfeit" stackId="a" fill="#dc2626" radius={[0, 4, 4, 0]} name="Counterfeit" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Scans by Region</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={regions.map(r => {
                  const rt = brandRegionData.regionTotals[r] || { scans: 0, authentic: 0, counterfeit: 0 }
                  return { region: r, authentic: rt.authentic, counterfeit: rt.counterfeit }
                }).sort((a, b) => (b.authentic + b.counterfeit) - (a.authentic + a.counterfeit))} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <YAxis dataKey="region" type="category" tick={{ fontSize: 11, fill: '#6b7280' }} width={100} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Bar dataKey="authentic" stackId="a" fill="#16a34a" name="Authentic" />
                  <Bar dataKey="counterfeit" stackId="a" fill="#dc2626" radius={[0, 4, 4, 0]} name="Counterfeit" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Risk Combinations</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[...brandRegionData.rows]
                .sort((a, b) => b.counterfeit_rate - a.counterfeit_rate)
                .slice(0, 6)
                .map((r, i) => (
                  <div key={i} className={`rounded-2xl p-4 border ${r.counterfeit_rate > 25 ? 'border-red-200 bg-red-50' : r.counterfeit_rate > 15 ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-900">{r.brand}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.counterfeit_rate > 25 ? 'bg-red-200 text-red-800' : r.counterfeit_rate > 15 ? 'bg-amber-200 text-amber-800' : 'bg-green-200 text-green-800'}`}>
                        {r.counterfeit_rate}% CF
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">{r.region} - {r.total_scans} scans, {r.counterfeit} counterfeits</p>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Analytics
